import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { EVERYDAY_SPEND_ENABLED } from '@features/everyday-spend/tokens/unleash-flags.token';
import { LoyaltyProgramApiService } from '@features/loyalty-program/services/loyalty-program-api/loyalty-program-api.service';
import { UnleashService } from '@karelics/angular-unleash-proxy-client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { getLocaleSelected } from '@rd/core/locale/store/selectors/locale.selectors';
import { IconRegistryService } from '@rd/core/services/icon-registry/icon-registry.service';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import { CoreState } from '@rd/core/store/reducers';
import { TENANT_NAME } from '@rd/core/tokens/rd-lib.token';
import { MERCHANDISE_MAINTENANCE_UNLEASH_KEY } from '@rd/core/tokens/unleash-flags.token';
import { Locale, LoyaltyProgram } from '@rd/types';
import { CmsPageContentType } from '@rd/types/cms.type';
import { isNotNull } from '@rd/utils/isNotNull';
import { filter, Observable, Subscription } from 'rxjs';
import { PageUrlSegment } from '../../types';
import {
  getMainUrlSegment,
  getUrlParamId,
} from '../../utils/anz-page-discovery.utils';

type PageUrlSegmentData = {
  [pageUrlSegment in PageUrlSegment]?: string;
};

@UntilDestroy()
@Component({
  selector: 'rd-anz-info-panel-accordion',
  templateUrl: './info-panel-accordion.component.html',
  styleUrls: ['./info-panel-accordion.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AnzInfoPanelAccordionComponent implements OnInit {
  private cmsFacade = inject(CmsFacade);
  private unleashService = inject(UnleashService);
  private isEverydaySpendEnabled = inject(EVERYDAY_SPEND_ENABLED);

  @HostBinding('class.rd-anz-info-panel-accordion') componentClass = true;
  urlMap: PageUrlSegmentData;
  currentTerms: string | undefined;
  currentLocale$: Subscription;
  currentLocale: Locale;
  toggleIcon: string;
  panelState = 1;
  @ViewChild(MatExpansionPanel) matExpansionPanelElement: MatExpansionPanel;

  constructor(
    @Inject(TENANT_NAME) private tenantName: string,
    private cdr: ChangeDetectorRef,
    private iconRegistryService: IconRegistryService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<CoreState>,
    private windowRefService: WindowRefService,
    private loyaltyProgramApiService: LoyaltyProgramApiService,
  ) {
    this.currentLocale$ = this.store
      .select(getLocaleSelected)
      .subscribe((locale: Locale | null) => {
        if (locale) {
          this.currentLocale = locale;
        }
      });
    this.toggleIcon = this.getToggleIconName('down');
    this.initializeUrlMap();
  }

  ngOnInit(): void {
    this.iconRegistryService.registerIcon('chevron-up');
    this.listenUrlChange();
  }

  triggerOpenedPanelStateEvent(resetIndex: number): void {
    this.panelState = resetIndex;
    this.panelState++;
    this.toggleIcon = this.getToggleIconName();
    this.cdr.detectChanges();
  }

  triggerClosedPanelStateEvent(): void {
    this.panelState--;
    this.toggleIcon = this.getToggleIconName();
  }

  get accordionDisplay(): string {
    return this.currentTerms ? 'block' : 'none';
  }

  private listenUrlChange(): void {
    // When user is being redirected to RD from GH (either on login or logout)
    // Angular router.events subscription will not be fired.
    // Therefore we need to call setCurrentTerms() once inside listenUrlChange()
    // Subsequant router changes will be handled by angular.router.events subscription
    this.setCurrentTerms(this.windowRefService.nativeWindow.location.pathname);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.setCurrentTerms(event.urlAfterRedirects);
        // This is to keep the accordion open by default
        // Whenever user navigates to different page
        this.matExpansionPanelElement.open();
      }
    });
  }

  private getToggleIconName(initialValue?: string): string {
    const direction = initialValue || this.panelState === 0 ? `down` : `up`;
    return `icon-chevron-${direction}`;
  }

  private identifyTermsPageFromUrl(mainUrlSegment: string): string {
    // @ts-ignore TS7053: expression of type 'string' can't be used to index
    return this.urlMap[mainUrlSegment];
  }

  // Initializing a map for each page with the
  // Segment of URL as a key and unique ID as a value
  private initializeUrlMap(): void {
    this.urlMap = {
      [PageUrlSegment.TRANSFER_LANDING]: 'transfer-details',
      [PageUrlSegment.TRANSFER_REVIEW]: 'transfer-review',
      [PageUrlSegment.MERCHANDISE_DETAILS]: 'merchandise-details',
      [PageUrlSegment.MANAGE_REDEMPTIONS]: 'orders-manage-auto-redemption',
      [PageUrlSegment.CAMPAIGN_PAGE]: this.buildTermsIdForCampaignPages(),
      [PageUrlSegment.EVERYDAY_SPEND_REVIEW]: 'edr-review',
      [PageUrlSegment.EVERYDAY_SPEND_CHECKOUT]: 'edr-checkout',
    };
  }

  // Create the matching unique ID for the termsMap
  // Using the main url segment so that we can get the current terms
  // Value from the urlMap. This unique ID should match the name value
  // added in the cms content
  // Format is {anz/anz-biz}-rd-{unique-id}
  private getTermsKey(
    mainUrlSegment: string,
    loyaltyProgram?: LoyaltyProgram,
  ): string {
    const termsKey = `${this.tenantName}-rd-${this.identifyTermsPageFromUrl(
      mainUrlSegment,
    )}`;
    if (loyaltyProgram) {
      return `${termsKey}-${loyaltyProgram.transferConnectIdentifier.toLowerCase()}`;
    } else {
      return termsKey;
    }
  }

  private setCurrentTerms(url: string): void {
    const mainUrlSegment = getMainUrlSegment(url) as PageUrlSegment;
    const pagesWithPartnerSpecificTerms = [
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.TRANSFER_LANDING,
    ];
    if (this.showTermsInCurrentPage(mainUrlSegment)) {
      if (pagesWithPartnerSpecificTerms.includes(mainUrlSegment)) {
        this.getLoyaltyProgramUsingId(mainUrlSegment).subscribe(
          (program: LoyaltyProgram) => {
            const termsKey = this.getTermsKey(mainUrlSegment, program);
            this.getUpdatedTerms(termsKey);
          },
        );
      } else {
        const termsKey = this.getTermsKey(mainUrlSegment);
        this.getUpdatedTerms(termsKey);
      }
    } else {
      this.currentTerms = '';
    }
    this.cdr.markForCheck();
  }

  // Returns true if the terms should be rendered in the given page,
  // By checking all the values in the PageUrlSegment enum list
  private showTermsInCurrentPage(url: string): boolean {
    if (
      this.unleashService.isEnabled(MERCHANDISE_MAINTENANCE_UNLEASH_KEY) &&
      url === PageUrlSegment.MERCHANDISE_DETAILS
    )
      return false;

    const infoPanelEnabledRoutes: PageUrlSegment[] = [
      PageUrlSegment.TRANSFER_LANDING,
      PageUrlSegment.TRANSFER_REVIEW,
      PageUrlSegment.CAMPAIGN_PAGE,
      PageUrlSegment.MERCHANDISE_DETAILS,
      PageUrlSegment.MANAGE_REDEMPTIONS,
      ...(this.isEverydaySpendEnabled
        ? [
            PageUrlSegment.EVERYDAY_SPEND_REVIEW,
            PageUrlSegment.EVERYDAY_SPEND_CHECKOUT,
          ]
        : []),
    ];
    const matchedSegment: PageUrlSegment[] = Object.values(
      infoPanelEnabledRoutes,
    ).filter((segment) => segment === url);
    return matchedSegment.length > 0;
  }

  private getUpdatedTerms(termsKey: string): void {
    this.cmsFacade
      .getContentByName({
        pageName: termsKey,
        pageType: 'terms',
        locale: this.currentLocale.code,
      })
      .pipe(untilDestroyed(this), filter(isNotNull))
      .subscribe((termsContent: CmsPageContentType) => {
        if (termsContent.type === 'terms') {
          this.currentTerms = termsContent?.content;
          this.cdr.markForCheck();
        }
      });
  }

  private getLoyaltyProgramUsingId(
    mainUrlSegment: PageUrlSegment,
  ): Observable<LoyaltyProgram> {
    const delimeter =
      mainUrlSegment === PageUrlSegment.TRANSFER_LANDING ||
      mainUrlSegment === PageUrlSegment.EVERYDAY_SPEND_CHECKOUT
        ? '/'
        : '=';
    const programId = getUrlParamId(
      this.windowRefService.nativeWindow.location.href,
      delimeter,
    );
    return this.loyaltyProgramApiService.getLoyaltyProgram(programId);
  }

  private buildTermsIdForCampaignPages(): string {
    const url = this.windowRefService.nativeWindow.location.href;
    return `campaign-page-terms-${url.split('/').pop()}`;
  }
}
